import styled from '@emotion/styled';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { navigationService, userAuthenticator } from 'src/config/rootService';
import logoImg from 'src/public/img/mh-logo-text.svg';
import { Input } from 'src/shared/components/Input';
import { Button } from 'src/shared/ds/components/Button';
import { ButtonGroup } from 'src/shared/ds/components/ButtonGroup';
import { isEmailValid } from 'src/shared/helpers/emailValidator';
import { EapAuthLayout } from 'src/shared/layout/EapAuthLayout';
import { color, font } from 'src/shared/styles/variables';

import { Input as NewInput, InputType } from '../../../shared/ds/components/Input';
import { AuthorizationBox } from '../components/AuthorizationBox';

const Container = styled.div({
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
    alignItems: 'start',
    flexGrow: 1,
    paddingTop: '110px',
});
const ContentContainer = styled.div({
    textAlign: 'center',
    maxWidth: '500px',
    flexDirection: 'column',
});
const Title = styled.h1(
    {
        color: color.ebonyClay,
        lineHeight: '28px',
        textAlign: 'left',
        paddingBottom: '16px',
        margin: 0,
    },
    font.heavy(font.size.extraLarge),
);
interface EmailSentSuccessfully {
    emailSentSuccessfully: boolean;
}
const Subtitle = styled.h3<EmailSentSuccessfully>(
    ({ emailSentSuccessfully }) => ({
        color: color.blueBayoux,
        lineHeight: '24px',
        paddingBottom: `${emailSentSuccessfully ? 0 : '24px'}`,
        marginBottom: 0,
        textAlign: 'left',
    }),
    font.book(font.size.medium),
);
interface ButtonsContainerProps {
    singleButton: boolean;
}
const ButtonsContainer = styled.div<ButtonsContainerProps>(({ singleButton }) => {
    return {
        paddingTop: '40px',
        width: `${singleButton ? '240px' : '100%'} !important`,
        display: singleButton ? 'flex' : 'block',
    };
});
const BrandImage = styled.img({
    marginLeft: 32,
    marginTop: 20,
    width: '15em',
    cursor: 'pointer',
    marginRight: 12,
});
export enum EapPasswordResetFormTestId {
    EmailField = 'EapPasswordResetFormTestId.EmailField',
    OldEmailField = 'EapPasswordResetFormTestId.OldEmailField',
    BackButton = 'EapPasswordResetFormTestId.BackButton',
}

const EapPasswordResetForm: React.FC = () => {
    const [email, setEmail] = useState('');
    const [emailValid, setEmailValid] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [requestInProgress, setRequestInProgress] = useState(false);
    const [emailSentSuccessfully, setEmailSentSuccessfully] = useState(false);
    const searchParams = new URLSearchParams(window.location.search);
    const { t } = useTranslation();
    const { useRegistrationRevamp } = useFlags();

    const handleEmailChange = (value: string) => {
        setEmailValid(isEmailValid(value));
        if (errorMessage) {
            setErrorMessage('');
        }

        setEmail(value.trim());
    };

    const handleSubmit = async () => {
        setRequestInProgress(true);

        try {
            await userAuthenticator.requestPasswordResetEmail(email);

            setEmailSentSuccessfully(true);
            setEmail('');
        } catch (error) {
            setErrorMessage(error);
            setEmailSentSuccessfully(false);
        }

        setRequestInProgress(false);
    };

    return useRegistrationRevamp ? (
        <>
            <BrandImage
                src={logoImg}
                onClick={() => {
                    window.location.href = 'https://www.modernhealth.com';
                }}
            />
            <Container>
                <ContentContainer>
                    <Title>{t('auth:password_reset_form.password_reset')}</Title>
                    <Subtitle emailSentSuccessfully={emailSentSuccessfully}>
                        {emailSentSuccessfully
                            ? t('auth:password_reset_form.email_sent_successfully')
                            : t('auth:password_reset_form.additional_information')}
                    </Subtitle>
                    {!emailSentSuccessfully && (
                        <NewInput
                            fieldId="email"
                            label={t('auth:common_form_inputs.labels.email')}
                            type={InputType.TextField}
                            value={email}
                            error={errorMessage}
                            onChange={value => handleEmailChange(value.toString())}
                            testID={EapPasswordResetFormTestId.EmailField}
                        />
                    )}
                    {/* This should be enhanced with https://joinmodernhealth.atlassian.net/browse/FOUNDATION-305*/}
                    <ButtonsContainer singleButton={emailSentSuccessfully}>
                        {emailSentSuccessfully ? (
                            <Button
                                onClick={() => navigationService.navigate('/login')}
                                variant="secondary"
                                size="large"
                                title={t('auth:login_form.back_to_login')}
                                testID={EapPasswordResetFormTestId.BackButton}
                                fullWidth
                            />
                        ) : (
                            <ButtonGroup
                                firstButtonText={t('auth:password_reset_form.back')}
                                secondButtonText={t('auth:password_reset_form.send')}
                                onFirstButtonClick={() => navigationService.navigate('/login')}
                                onSecondButtonClick={handleSubmit}
                                secondButtonDisabled={!emailValid}
                            />
                        )}
                    </ButtonsContainer>
                </ContentContainer>
            </Container>
        </>
    ) : (
        <EapAuthLayout>
            <AuthorizationBox
                title={t('auth:password_reset_form.password_reset')}
                buttonText={t('auth:password_reset_form.send')}
                additionalInfo={t('auth:password_reset_form.additional_information')}
                noContent={emailSentSuccessfully}
                isFetching={requestInProgress}
                onSubmit={handleSubmit}
                showBackToLogin
                useProviderLogin={searchParams && searchParams.get('role') == 'COACH'}
            >
                {emailSentSuccessfully ? (
                    <h3 style={{ margin: '50px 0' }}>
                        {t('auth:password_reset_form.email_sent_successfully')}
                    </h3>
                ) : (
                    <Input
                        fieldId="email"
                        label={t('auth:common_form_inputs.labels.email')}
                        type="text"
                        value={email}
                        error={errorMessage}
                        onChange={handleEmailChange}
                        testID={EapPasswordResetFormTestId.OldEmailField}
                    />
                )}
            </AuthorizationBox>
        </EapAuthLayout>
    );
};

export default EapPasswordResetForm;
